import { useEffect, useRef, Suspense } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { useGLTF, useTexture, AccumulativeShadows, RandomizedLight, OrbitControls, Environment, Center, Html } from '@react-three/drei'
import { EffectComposer, N8AO, ToneMapping } from "@react-three/postprocessing"
import { ToneMappingMode } from 'postprocessing'
import { easing } from 'maath'
import { useSnapshot } from 'valtio'
import { state } from './store'

function Effects() {
  return (
    <EffectComposer stencilBuffer disableNormalPass autoClear={false} multisampling={0}>
      <N8AO halfRes aoSamples={5} aoRadius={0.4} distanceFalloff={0.75} intensity={1} />
      <ToneMapping mode={ToneMappingMode.ACES_FILMIC} exposure={1.35} whitePoint={16.0} />
    </EffectComposer>
  )
}

function Loader() {
  return (
    <Html center>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px',
        }} />
      </div>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Html>
  )
}

const queryParams = new URLSearchParams(window.location.search);

export const App = ({ position = [20, 0.5, 5], fov = 30 }) => {
  const ref = useRef();
  const shadowsRef = useRef()
  const snap = useSnapshot(state)
  const preset = Object.fromEntries(queryParams.entries()).preset || 'warehouse';

  // Store the default camera position
  useEffect(() => {
    state.defaultCameraPosition = position
  }, [])

  // Store OrbitControls reference in state
  useEffect(() => {
    if (ref.current) {
      state.orbitControlsRef = ref.current
    }
  }, [ref.current])

  useEffect(() => {
    if (shadowsRef.current) {
      shadowsRef.current.reset()
    }
  }, [snap.selectedModel, snap.showWheels, snap.showDoor, snap.showTV])

  return (
    <Canvas gl={{ antialias: false, preserveDrawingBuffer: true }} shadows camera={{ position, fov }} eventSource={document.getElementById('root')} eventPrefix="client">
      <Suspense fallback={<Loader />}>

        {/* Black and white stage environment */}
        <Environment preset={preset} environmentIntensity={0.3} />

        {/* Ambient lighting for base illumination */}
        <ambientLight intensity={0.5} color="#ffffff" />

        {/* Main directional light (key light) - creates main shadows */}
        <directionalLight
          castShadow
          position={[8, 18, 6]}
          intensity={2.0}
          color="#ffffff"
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-far={30}
          shadow-camera-left={-8}
          shadow-camera-right={8}
          shadow-camera-top={8}
          shadow-camera-bottom={-8}
          shadow-bias={-0.0001}
        />

        {/* Fill light from the front-left - softer shadows */}
        <directionalLight
          position={[-6, 14, 4]}
          intensity={1.0}
          color="#f8f9fa"
        />

        {/* Rim light from behind for depth */}
        {/* <directionalLight
          position={[0, 8, -8]}
          intensity={0.8}
          color="#ffffff"
        /> */}

        <Effects />

        <Center>
          <Model />
        </Center>
        <AccumulativeShadows
          shadows={shadowsRef}
          temporal
          frames={30}
          alphaTest={0.5}
          scale={12}
          resolution={1024}
          position={[0, -0.99, 0]}
          color="#000000"
          opacity={0.5}
        >
          <RandomizedLight position={[4, 8, 4]} />
        </AccumulativeShadows>
        <OrbitControls
          ref={ref}
          minPolarAngle={Math.PI / 8}
          maxPolarAngle={Math.PI / 2.0}
          target={[0, -0.2, 0]}
          enabled={!snap.isDraggingPanel}
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={3}
          maxDistance={8}
          // Limit horizontal rotation (azimuth) to 180° total (-90° to +90°)
          // minAzimuthAngle={-Math.PI / 2}
          // maxAzimuthAngle={Math.PI}
        />
      </Suspense>
    </Canvas>
  )
}

function Model(props) {
  const snap = useSnapshot(state)
  const dapapod = useGLTF('/Dapapod.glb')
  const originalTextures = useRef(null) // Store reference to original texture maps separately

  // Set up shadows and visibility on scene load
  useEffect(() => {
    if (dapapod.scene) {
      dapapod.scene.traverse((child) => {
        // Set shadow properties for all meshes
        if (child.isMesh) {
          child.castShadow = true
          child.receiveShadow = true
        }
      })

      // Store original material colors only when model first loads
      if (dapapod.materials && Object.keys(state.originalMaterials).length === 0) {
        const originalMaterials = {}

        // Initialize texture storage
        if (!originalTextures.current) {
          originalTextures.current = {}
        }

        Object.entries(dapapod.materials).forEach(([materialName, material]) => {
          // Store reference to the original texture map separately
          // This prevents it from being lost when we set material.map = null
          if (material.map) {
            originalTextures.current[materialName] = material.map
          }

          // Store color and essential properties
          originalMaterials[materialName] = {
            color: material.color ? material.color.clone() : null,
            roughness: material.roughness,
            metalness: material.metalness,
            opacity: material.opacity,
            transparent: material.transparent,
            emissive: material.emissive ? material.emissive.clone() : null
          }
        })
        state.originalMaterials = originalMaterials
      }
    }
  }, [dapapod.scene, dapapod.materials])

  // Update visibility based on state changes
  useEffect(() => {
    if (dapapod.scene) {
      dapapod.scene.traverse((child) => {
        // Control group visibility
        if (child.name === 'Standing_Pod') {
          child.visible = snap.selectedModel === 'StandingPod'
        }
        if (child.name === 'Single_Pod') {
          child.visible = snap.selectedModel === 'SinglePod'
        }
        if (child.name === 'Double_Pod') {
          child.visible = snap.selectedModel === 'DoublePod'
        }
        if (child.name === 'Quad_Pod') {
          child.visible = snap.selectedModel === 'QuadPod'
        }

        // Handle wheel visibility for both groups
        if (child.name && child.name.toLowerCase().includes('wheel')) {
          child.visible = snap.showWheels
        }

        // Handle TV visibility for multi-person pods
        if (child.name === 'TV_QP' || child.name === 'TV_DP') {
          child.visible = (snap.selectedModel === 'QuadPod' || snap.selectedModel === 'DoublePod') && snap.showTV
        }

        // Handle door visibility for Single_Pod
        if (child.parent) {
          const isDoorComponent = child.name.toLowerCase().includes('glass') || child.name.toLowerCase().includes('handle') || child.name.toLowerCase().includes('door') || child.name.toLowerCase().includes('inner_rim') || child.name.toLowerCase().includes('hinge');
          if (isDoorComponent) {
            child.visible = snap.showDoor
          }
        }
      })
    }
  }, [snap.selectedModel, snap.showWheels, snap.showDoor, snap.showTV, dapapod.scene])

  useFrame((_, delta) => {
    // Read directly from valtio proxy (not snap) so we always have current values.
    // snap is a frozen snapshot that only updates on re-render, but these properties
    // aren't accessed during render, so snap would be stale here.
    const currentSelectedMaterial = state.selectedMaterial
    const currentSelectedModel = state.selectedModel
    const currentPickedItems = state.pickedItems
    const currentOriginalMaterials = state.originalMaterials
    const currentFrostedGlass = state.frostedGlass

    // Apply material changes only if the user explicitly picked a color for the current material
    if (currentSelectedMaterial && dapapod.materials) {
      const pickedItem = currentPickedItems.find(
        item => item.material === currentSelectedMaterial && item.model === currentSelectedModel
      )

      // Iterate through all materials and apply color to matching ones
      Object.values(dapapod.materials).forEach(material => {
        if (material.name && material.name.toLowerCase().includes(currentSelectedMaterial.toLowerCase())) {
          if (pickedItem && pickedItem.color === 'Original Material') {
            // Restore original material properties (avoiding texture assignments)
            const originalMaterial = currentOriginalMaterials[material.name]
            if (originalMaterial) {
              // Restore color
              if (originalMaterial.color) {
                easing.dampC(material.color, originalMaterial.color, 0.25, delta)
              }

              // Restore basic material properties only
              if (originalMaterial.roughness !== undefined) material.roughness = originalMaterial.roughness
              if (originalMaterial.metalness !== undefined) material.metalness = originalMaterial.metalness
              if (originalMaterial.opacity !== undefined) material.opacity = originalMaterial.opacity
              if (originalMaterial.transparent !== undefined) material.transparent = originalMaterial.transparent

              // Restore emissive color if it exists
              if (originalMaterial.emissive && material.emissive) {
                material.emissive.copy(originalMaterial.emissive)
              }

              // Restore texture map from our separate texture storage
              const originalTexture = originalTextures.current?.[material.name]
              if (originalTexture) {
                material.map = originalTexture
              }

              material.needsUpdate = true
            }
          } else if (pickedItem && pickedItem.colorHex) {
            // Apply the explicitly selected color for this material only
            const materialNameLower = currentSelectedMaterial.toLowerCase()
            if (materialNameLower.startsWith('poly') || materialNameLower.startsWith('exterior') || materialNameLower.startsWith('internal table') || materialNameLower.startsWith('internal seating')) {
              // Remove texture maps to get solid color
              material.map = null
              material.needsUpdate = true
            }
            easing.dampC(material.color, pickedItem.colorHex, 0.25, delta)
          }
        }
      })
    }

    // Handle frosted glass effect based on selected model
    if (currentSelectedModel === 'StandingPod') {
      const glassMat = dapapod.materials['Glass Material 3']
      if (glassMat) {
        if (currentFrostedGlass) {
          glassMat.roughness = 1.0
          glassMat.metalness = 0.1
          glassMat.transmission = 0.3
          glassMat.opacity = 0.5
          glassMat.transparent = true
          glassMat.ior = 1.5
          glassMat.thickness = 0.5
          glassMat.reflectivity = 0.3
        } else {
          glassMat.roughness = 0.0
          glassMat.metalness = 0.05
          glassMat.transmission = 1.0
          glassMat.opacity = 0.1
          glassMat.transparent = true
          glassMat.ior = 1.52
          glassMat.thickness = 0.1
          glassMat.reflectivity = 0.3
        }
        glassMat.needsUpdate = true
      }
    }

    if (currentSelectedModel === 'SinglePod') {
      const glassMat = dapapod.materials['Glass Material']
      if (glassMat) {
        if (currentFrostedGlass) {
          glassMat.roughness = 1.0
          glassMat.metalness = 0.1
          glassMat.transmission = 0.3
          glassMat.opacity = 0.5
          glassMat.transparent = true
          glassMat.ior = 1.5
          glassMat.thickness = 0.5
          glassMat.reflectivity = 0.3
        } else {
          glassMat.roughness = 0.0
          glassMat.metalness = 0.05
          glassMat.transmission = 1.0
          glassMat.opacity = 0.1
          glassMat.transparent = true
          glassMat.ior = 1.52
          glassMat.thickness = 0.1
          glassMat.reflectivity = 0.3
        }
        glassMat.needsUpdate = true
      }
    }

    if (currentSelectedModel === 'DoublePod') {
      // Handle frosted glass effect for DoublePod
      const glassMat = dapapod.materials['Glass Material 2']
      if (glassMat) {
        if (currentFrostedGlass) {
          glassMat.roughness = 1.0
          glassMat.metalness = 0.1
          glassMat.transmission = 0.3
          glassMat.opacity = 0.5
          glassMat.transparent = true
          glassMat.ior = 1.5
          glassMat.thickness = 0.5
          glassMat.reflectivity = 0.3
        } else {
          glassMat.roughness = 0.0
          glassMat.metalness = 0.05
          glassMat.transmission = 1.0
          glassMat.opacity = 0.1
          glassMat.transparent = true
          glassMat.ior = 1.52
          glassMat.thickness = 0.1
          glassMat.reflectivity = 0.3
        }
        glassMat.needsUpdate = true
      }
    }

    if (currentSelectedModel === 'QuadPod') {
      // Handle frosted glass effect for QuadPod
      const glassMat = dapapod.materials['Glass Material 1']
      if (glassMat) {
        if (currentFrostedGlass) {
          glassMat.roughness = 1.0
          glassMat.metalness = 0.1
          glassMat.transmission = 0.3
          glassMat.opacity = 0.5
          glassMat.transparent = true
          glassMat.ior = 1.5
          glassMat.thickness = 0.5
          glassMat.reflectivity = 0.3
        } else {
          glassMat.roughness = 0.0
          glassMat.metalness = 0.05
          glassMat.transmission = 1.0
          glassMat.opacity = 0.1
          glassMat.transparent = true
          glassMat.ior = 1.52
          glassMat.thickness = 0.1
          glassMat.reflectivity = 0.3
        }
        glassMat.needsUpdate = true
      }
    }
  })
  return (
    <group {...props} dispose={null}>
      {/* Load the entire Dapapod scene */}
      <primitive
        object={dapapod.scene}
        position={[0, snap.showWheels ? 0 : -0.14, 0]}
        rotation={[Math.PI / 2, Math.PI / 2, -Math.PI / 2]}
        scale={1}
      />
    </group>
  )
}

useGLTF.preload('/Dapapod.glb')
  ;['/StandingPod.png', '/SinglePod.png', '/DoublePod.png', '/QuadPod.png'].forEach(useTexture.preload)
